import { memo } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { CheckCircle as CheckCircleIcon, Warning as WarningIcon } from '@mui/icons-material';
import SnackbarAlert from '../../../shared/components/SnackbarAlert';
import BindingRowEditor from './BindingRowEditor';
import { useBindingEditor } from './useBindingEditor';
import type { BindingEditorType } from './useBindingEditor';

interface Props {
  service: string;
  productName: string;
  versionName: string;
}

const BindingEditor = memo<Props>(({ service, productName, versionName }) => {
  const {
    parameters,
    draft,
    resolvers,
    loading,
    resolversLoading,
    submitting,
    bindingsComplete,
    unboundCount,
    dirty,
    setType,
    setLiteral,
    setResolver,
    setRequired,
    setDeploymentOutput,
    save,
    snackbar,
    closeSnackbar,
  } = useBindingEditor({ service, productName, versionName });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (parameters.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2, fontStyle: 'italic' }}>
        No CFN parameters for this version.
      </Typography>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, pr: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Parameter bindings
          </Typography>
          {bindingsComplete ? (
            <Chip icon={<CheckCircleIcon />} label="Bindings complete" color="success" size="small" variant="outlined" />
          ) : (
            <Chip icon={<WarningIcon />} label={`${unboundCount} binding${unboundCount !== 1 ? 's' : ''} needed`} color="warning" size="small" variant="outlined" />
          )}
        </Box>
        <Button
          variant="contained"
          size="small"
          onClick={save}
          disabled={submitting || !dirty}
          startIcon={submitting ? <CircularProgress size={14} /> : undefined}
        >
          {submitting ? 'Saving…' : 'Save bindings'}
        </Button>
      </Box>

      {/* Column headers */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '180px 200px 1fr 120px',
          gap: 1.5,
          pb: 0.5,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        {(['CFN key', 'Type', 'Value / config', 'Constraints'] as const).map((h) => (
          <Typography key={h} variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
            {h}
          </Typography>
        ))}
      </Box>

      {parameters.map((param, idx) => (
        <Box key={param.cfn_key}>
          <BindingRowEditor
            param={param}
            draft={draft[param.cfn_key] ?? { type: 'literal', value: '' }}
            resolvers={resolvers}
            resolversLoading={resolversLoading}
            disabled={submitting}
            onTypeChange={(key, type) => setType(key, type as BindingEditorType)}
            onLiteralChange={setLiteral}
            onResolverChange={setResolver}
            onRequiredChange={setRequired}
            onDeploymentOutputChange={setDeploymentOutput}
          />
          {idx < parameters.length - 1 && <Divider />}
        </Box>
      ))}

      <SnackbarAlert snackbar={snackbar} onClose={closeSnackbar} />
    </Box>
  );
});

BindingEditor.displayName = 'BindingEditor';
export default BindingEditor;
